import { OAuthCredentialsService } from '@tableau/oauth-credentials-service';
import { ActivitySpinnerWidget, ButtonProps, ButtonType, ButtonWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, InlineAlertType, InlineAlertWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IOAuthCredential, OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ConnectToSalesforceDropdown } from '~src/settings/IntegrationsSettings/components/ConnectToSalesforceDropdown';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { getSalesforceAppParams, GetSalesforceAppsService as GetSalesforceAppsService, IGetSalesforceAppResult, ISalesforceApp } from '../restApi/requests/GetSalesforceApps';
import { IPublishToSalesforceResponse, publishToSalesforceRequest, PublishToSalesforceService } from '../restApi/requests/PublishToSalesforce';
import { PublishToSalesforceDialogStyles } from '../styles/PublishToSalesforceDialogStyles';
import { getInitialCloseDialogTelemetry, PublishToSalesforceDialogErrorMessages, PublishToSalesforceDialogErrorTypes, PublishToSalesforceTelemetryParams, sendDialogErrorsTelemetry, sendExistingCredentialsDialogTelemetry, sendNoCredentialsDialogTelemetry } from '../telemetry/PublishToSalesforceTelemetry';
import { PublishToSalesforceResponseHandler } from '../utils/PublishToSalesforceResponseHandler';
import { PublishToSalesforceToaster } from '../utils/PublishToSalesforceToaster';
import { SalesforceAppsView } from './SalesforceAppsView';

export const PublishToSalesforceDialogTestIds = {
  PermissionsDescription: 'publish-salesforce-permissions-description',
  CancelButton: 'publish-to-salesforce-dialog-cancel',
  PublishButton: 'publish-to-salesforce-dialog-confirm',
  Dialog: 'publish-to-salesforce-dialog',
  FooterButtons: 'cancel-publish-dialog-footer',
  CredentialsButton: 'salesforce-credentials-button',
  CredentialsDropdown: 'salesforce-credentials-dropdown',
  LearnMoreHelpLink: 'learn-more-link',
  SuccessToast: 'success-toast',
  ActivitySpinner: 'publish-to-salesforce-spinner',
  AlertWidget: 'alert-widget',
  LoadAppsAlertWidget: 'load-apps-alert-widget',
  LoadMoreAppsButton: 'load-more-apps-button',
};

export interface PublishToSalesforceDialogProps {
  views: IContentInfo[];
  sessionManagementService: SessionManagementService;
  close: () => void;
}

export interface PublishToSalesforceDialogState {
  credentials: IOAuthCredential[];
  selectedCredential: IOAuthCredential;
  selectedApp: ISalesforceApp;
  apps: ISalesforceApp[];
  isWaitingOnApps: boolean;
  isWaitingOnCredentials: boolean;
  hasCriticalError: boolean; // True in event of an error that precludes any user interaction with the dialog besides exiting
  alertMessage: string;
  isWaitingOnPublish: boolean;
  nextPageToken: string;
}

export const learnMoreToken: string = 'online-publish-to-salesforce';

export const minTimeToLiveInSeconds: number = 5 * 60; // Only get credentials with at least 5 mins left to live

export const aliveSalesforceCredentialsQuery: ResourceQuery<IOAuthCredential> = OAuthCredentials
  .allAliveSalesforce()
  .addFilter(Filter.fieldEq('type', OAuthCredentialTypes.Salesforce))
  .withExtraParams({ minTimeToLiveInSeconds });

const maxViewsAllowedToPublish = 25;

export class PublishToSalesforceDialog extends React.Component<PublishToSalesforceDialogProps, PublishToSalesforceDialogState> {
  constructor(props: PublishToSalesforceDialogProps) {
    super(props);
    let initialHasCriticalError: boolean = false;
    let initialAlertMessage: string = null;
    if (!this.isViewSelectionValid()) {
      initialHasCriticalError = true;
      initialAlertMessage = Messages.publishToSalesforceExceedMaxViewsError();
    }
    PublishToSalesforceTelemetryParams.closePublishDialogParams = getInitialCloseDialogTelemetry();
    this.state = {
      credentials: [], apps: [], selectedCredential: null, selectedApp: null, isWaitingOnApps: false, isWaitingOnCredentials: true,
      hasCriticalError: initialHasCriticalError, alertMessage: initialAlertMessage, isWaitingOnPublish: false, nextPageToken: null
    };
  }

  public async componentDidMount() {
    if (!this.isViewSelectionValid()) {
      sendDialogErrorsTelemetry(this.props.sessionManagementService, PublishToSalesforceDialogErrorTypes.exceedsViewsSelectedLimit, PublishToSalesforceDialogErrorMessages.exceedsViewsError);
    } else {
      await this.getOauthCredentials();
      if (this.state.credentials?.length) {
        await this.getSalesforceAppsAndUpdateState(this.state.selectedCredential.id);
        PublishToSalesforceTelemetryParams.closePublishDialogParams.is_existing_credentials_dialog = true;
      } else {
        this.setState({ isWaitingOnCredentials: false });
      }
      PublishToSalesforceTelemetryParams.closePublishDialogParams.apps_had_next_page = this.state.nextPageToken != null;
    }
  }

  /**
   * Use the provided oauthId to find the credential in the provided array of credentials
   * or in the dialog's state if no credentials are provided
   */
  private findCredential(oauthId: string): IOAuthCredential {
    return this.state.credentials.find(credential => credential.id === oauthId);
  }

  /**
   * Gets the list of OAuth credentials which is used to update the dialog state.
   * If a new oauth id is provided, that will be used as the selected credential.
   */
  async getOauthCredentials() {
    this.setState({ isWaitingOnCredentials: true });
    let returnedCredentials: ListResult<IOAuthCredential>;
    try {
      returnedCredentials = await SliceableHelper.fetchAll(aliveSalesforceCredentialsQuery);
    } catch (e) {
      sendDialogErrorsTelemetry(this.props.sessionManagementService, PublishToSalesforceDialogErrorTypes.fetchCredentials, e);
      this.setState({
        hasCriticalError: true,
        alertMessage: Messages.publishToSalesforcePublishCannotRetrieveCredentialsError()
      });
    }

    if (_.isEmpty(returnedCredentials.errors)) {
      const selectedCredential = this.selectMostRecentlyAddedCredential(returnedCredentials.items);
      // Sort by ascending order
      const sortedCredentialsByUsername = returnedCredentials.items.sort((trail, lead) => (trail.username.toLowerCase() < lead.username.toLowerCase()) ? -1 : 1);
      this.setState({ credentials: sortedCredentialsByUsername, selectedCredential });
    } else {
      sendDialogErrorsTelemetry(this.props.sessionManagementService, PublishToSalesforceDialogErrorTypes.fetchCredentials, this.convertOauthErrorsToString(returnedCredentials.errors));
      this.setState({
        hasCriticalError: true,
        alertMessage: Messages.publishToSalesforcePublishCannotRetrieveCredentialsError()
      });
    }
  }

  private selectMostRecentlyAddedCredential(credentials: IOAuthCredential[]): IOAuthCredential {
    return credentials.length ? credentials[credentials.length - 1] : null;
  }

  private convertOauthErrorsToString(errors: IError[]) {
    return errors.map((error) => error.message ? error.message : `Error Code: ${error.code}`).toString();
  }

  /**
   * Handles adding new Salesforce credential
   */
  public handleConnectToSalesforce = (): void => {
    OAuthCredentialsService.AddCredential({
      bypassBeforeConnectingDialog: true,
      connectionType: OAuthCredentialTypes.Salesforce,
      onCancel: () => { },
      onCredentialAdded: this.handleOnCredentialsAdded.bind(this),
      shouldKeepWindowOpen: false,
      sendEventFn: () => { },
      sessionLanguage: this.props.sessionManagementService.sessionLanguage(),
      sessionServerVersionBuild: this.props.sessionManagementService.sessionServerVersion().build,
      sessionServerVersionPlatform: this.props.sessionManagementService.sessionServerVersion().platform,
      connectionTypeDisplay: Messages.salesforce(),
      helpServiceExternalVersion: undefined,
      supportsGenericAuth: false,
    });
  };

  /**
   * Handles updates to dialog state once a new credential has been added
   */
  handleOnCredentialsAdded = async (addedOauthId: string, _credentialProperties: [string, any]) => {
    const noSavedCredentials: boolean = !this.hasSavedCredentials();
    await this.getOauthCredentials();
    PublishToSalesforceTelemetryParams.closePublishDialogParams.new_credential_added = true;
    this.setState({ selectedApp: null, apps: [], alertMessage: null });
    await this.getSalesforceAppsAndUpdateState(addedOauthId);
    if (noSavedCredentials) { this.sendNoCredentialsTelemetry(); }
  };

  private sendNoCredentialsTelemetry() {
    PublishToSalesforceTelemetryParams.closePublishDialogParams.no_credentials_dialog_cancelled = false;
    sendNoCredentialsDialogTelemetry(PublishToSalesforceTelemetryParams.closePublishDialogParams);
    PublishToSalesforceTelemetryParams.closePublishDialogParams.is_existing_credentials_dialog = true;

    // Reset the value for user flow of the existing credentials telemetry event
    PublishToSalesforceTelemetryParams.closePublishDialogParams.new_credential_added = false;
  }

  /**
   * Handles updates to dialog state once a credential has been selected
   */
  public handleSelectedCredentials = async (selectedOauthId: number): Promise<boolean> => {
    if (selectedOauthId !== parseInt(this.state.selectedCredential.id)) {
      let selectedCredential = this.findCredential(selectedOauthId.toString());
      this.setState({ selectedCredential, selectedApp: null, apps: [], alertMessage: null });
      PublishToSalesforceTelemetryParams.closePublishDialogParams.existing_credential_clicked = true;
      await this.getSalesforceAppsAndUpdateState(selectedOauthId.toString());
      return true;
    }
    return false;
  };

  /**
   * Used to determine which form of the dialog to render
   */
  private hasSavedCredentials(): boolean {
    return this.state.credentials?.length > 0;
  }

  private isViewSelectionValid(): boolean {
    // We currently don't allow batch publish for more than 25 views
    return this.props.views?.length <= maxViewsAllowedToPublish;
  }

  private renderDialogDescription() {
    const numViews = this.props.views.length;

    return <div className={PublishToSalesforceDialogStyles.PermissionsDescriptionSection} data-tb-test-id={PublishToSalesforceDialogTestIds.PermissionsDescription}>
      <p>{Messages.publishToSalesforceDialogDescription({ ITEM_COUNT: numViews, ITEM_COUNT_LOC: Formatters.formatCount(numViews) })}
        <TextLinkWidget
          testId={PublishToSalesforceDialogTestIds.LearnMoreHelpLink}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory(learnMoreToken)}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.publishToSalesforceDialogLearnMore()}
        </TextLinkWidget>
      </p>
    </div>;
  }

  private renderCredentialsSection() {
    if (this.hasSavedCredentials()) {
      // Render credentials dropdown
      const buttonText = this.state.selectedCredential ? this.state.selectedCredential.username : null;
      return (
        <div>
          <div>
            <p>{Messages.publishToSalesforceDialogSelectCredentials()}</p>
          </div>
          <div className={PublishToSalesforceDialogStyles.CredentialsDropdown}>
            <ConnectToSalesforceDropdown
              credentials={this.state.credentials}
              handleSelectedItem={this.handleSelectedCredentials.bind(this)}
              handleAddNewCredentials={this.handleConnectToSalesforce}
              customButtonText={buttonText}
            />
          </div>
        </div>);
    } else {
      // Render credentials button
      return <div className={PublishToSalesforceDialogStyles.CredentialsButton}>
        <ButtonWidget
          testId={PublishToSalesforceDialogTestIds.CredentialsButton}
          handleClick={this.handleConnectToSalesforce}
          buttonType={ButtonType.Primary}
        >
          {Messages.connectToSalesforce_Title()}
        </ButtonWidget>
      </div>;
    }
  }

  private renderAppPicker() {
    if (this.hasSavedCredentials() && this.state.apps.length > 0) {
      return <div>
        <div className={PublishToSalesforceDialogStyles.PublishToSectionLabel}>
          {Messages.publishToSalesforceDialogPublishTo()}
        </div>
        <SalesforceAppsView
          salesforceApps={this.state.apps}
          onSelect={this.handleSelectedApp}
          selectedAppValue={this.state.selectedApp?.id}
        />
        {this.state.nextPageToken &&
          <InlineAlertWidget
            className={PublishToSalesforceDialogStyles.LoadMoreAppsWidgetSection}
            children={Messages.publishToSalesforceDialogLoadMoreApps()}
            type={InlineAlertType.Info}
            testId={PublishToSalesforceDialogTestIds.LoadAppsAlertWidget}
            button={this.loadMoreAppsButton()}
          />}
      </div>;
    }
  }

  private renderAlertWidget() {
    if (this.state.alertMessage != null) {
      return (
        <div className={PublishToSalesforceDialogStyles.AlertWidgetSection}>
          <InlineAlertWidget
            children={this.state.alertMessage}
            type={InlineAlertType.Error}
            testId={PublishToSalesforceDialogTestIds.AlertWidget}
          />
        </div>);
    }
  }

  private loadMoreAppsButton() {
    return <ButtonWidget
      children={Messages.publishToSalesforceDialogLoadMoreButton()}
      onClick={this.onLoadMoreApps}
      testId={PublishToSalesforceDialogTestIds.LoadMoreAppsButton}
      showSpinner={this.state.apps && this.state.apps.length && this.state.isWaitingOnApps}
    />;
  }

  onLoadMoreApps: () => void = async () => {
    await this.getSalesforceAppsAndUpdateState(this.state.selectedCredential.id, this.state.nextPageToken);
    PublishToSalesforceTelemetryParams.closePublishDialogParams.num_times_more_apps_loaded++;
  };

  private canPublish() {
    return this.state.selectedApp && this.state.selectedCredential;
  }

  private sendTelemetryAndClose = (): void => {
    if (!this.hasSavedCredentials()) {
      PublishToSalesforceTelemetryParams.closePublishDialogParams.no_credentials_dialog_cancelled = true;
      sendNoCredentialsDialogTelemetry(PublishToSalesforceTelemetryParams.closePublishDialogParams);
    } else {
      sendExistingCredentialsDialogTelemetry(PublishToSalesforceTelemetryParams.closePublishDialogParams);
    }
    this.props.close();
  };

  private cancelButtonProps(): ButtonProps {
    return {
      handleClick: this.sendTelemetryAndClose,
      buttonType: ButtonType.Outline,
      children: Messages.cancel(),
      testId: PublishToSalesforceDialogTestIds.CancelButton,
      disabled: this.state.isWaitingOnPublish
    };
  }

  onPublish: () => void = async () => {
    const selectedViewLuids: string[] = this.props.views.map((view) => view.luid);
    PublishToSalesforceTelemetryParams.closePublishDialogParams.existing_credentials_dialog_cancelled = false;
    PublishToSalesforceTelemetryParams.closePublishDialogParams.clicked_publish = true;
    await this.publishViewsToSalesforce(selectedViewLuids, this.state.selectedApp.id, this.state.selectedCredential.id);
  };

  private getPublishButtonProps(): ButtonProps {
    return {
      disabled: !this.canPublish(),
      handleClick: this.onPublish,
      buttonType: ButtonType.Primary,
      children: Messages.publish(),
      testId: PublishToSalesforceDialogTestIds.PublishButton,
      showSpinner: this.state.isWaitingOnPublish
    };
  }

  private renderFooterButtons() {
    return <DialogFooterButtonsWidget
      buttons={[this.cancelButtonProps(), this.getPublishButtonProps()]}
      testId={PublishToSalesforceDialogTestIds.FooterButtons}
    />;
  }

  private renderActivitySpinner() {
    return <div className={PublishToSalesforceDialogStyles.ActivitySpinnerStyle}>
      <ActivitySpinnerWidget testId={PublishToSalesforceDialogTestIds.ActivitySpinner} shouldShowUnderlay={false} />
    </div>;
  }

  // Logic for SalesforceApps

  /**
   * Handles updates to dialog state once a a salesforce app has been selected
   */
  public handleSelectedApp = (appId: string): void => {
    const selectedApp = this.state.apps.find((app) => app.id === appId);
    if (selectedApp) {
      this.setState({ selectedApp: selectedApp });
      PublishToSalesforceTelemetryParams.closePublishDialogParams.selected_app_id = selectedApp.id;
    }
  };

  /**
    * Making an api call to retrieve the salesforce apps, we set the state with the apps once we have them.
    * We show an error dialog if there's was an error while fetching the apps.
    */
  private async getSalesforceAppsAndUpdateState(oauthId: string, nextPageToken?: string) {
    const requestParams: getSalesforceAppParams = { oauthId, nextPageToken };
    this.setState({ isWaitingOnApps: true });
    try {
      const result: IGetSalesforceAppResult = await GetSalesforceAppsService.callViaApiService(requestParams);
      const salesforceApps = this.state.apps.concat(result.salesforceApps.salesforceApp);
      this.setState({ apps: salesforceApps, nextPageToken: result.salesforceApps.nextPageToken });
      PublishToSalesforceTelemetryParams.closePublishDialogParams.num_apps_returned = result.salesforceApps.salesforceApp?.length;
    } catch (e) {
      this.setState({ alertMessage: Messages.publishToSalesforcePublishCannotRetrieveAppsError() });
      sendDialogErrorsTelemetry(this.props.sessionManagementService, PublishToSalesforceDialogErrorTypes.getSalesforceApps, e);
    }
    if (this.state.apps.length === 0) {
      this.setState({ alertMessage: Messages.publishToSalesforcePublishCannotRetrieveAppsError() });
    }
    this.setState({ isWaitingOnCredentials: false, isWaitingOnApps: false });
  }

  private renderCredentialsSectionAndAppPicker() {
    return <div>
      {this.renderCredentialsSection()}
      {(!this.state.apps || !this.state.apps.length) && this.state.isWaitingOnApps ? this.renderActivitySpinner() : this.renderAppPicker()}
    </div>;
  }

  private renderDialogBody() {
    return <div>
      {this.state.isWaitingOnCredentials ? this.renderActivitySpinner() : this.renderCredentialsSectionAndAppPicker()}
    </div>;
  }

  /**
   * Make an api call to batch publish the selected views to the selected app.
   * Shows toasts depending on the outcome of the publish.
   */
  private async publishViewsToSalesforce(viewLuids: string[], salesforceAppId: string, oauthId: string) {
    const requestBody: publishToSalesforceRequest = {
      publishToSalesforce: {
        viewLuids,
        salesforceAppId,
        oauthId
      }
    };

    try {
      this.setState({ isWaitingOnPublish: true });
      let publishResponse: IPublishToSalesforceResponse = await PublishToSalesforceService.callViaApiService(requestBody);
      PublishToSalesforceResponseHandler.notifyUser(publishResponse, this.state.selectedApp, this.props.views, this.props.sessionManagementService);
    } catch (e) {
      PublishToSalesforceToaster.popGenericPublishError();
      sendDialogErrorsTelemetry(this.props.sessionManagementService, PublishToSalesforceDialogErrorTypes.publishToSalesforce, e);
    }
    if (!PublishToSalesforceTelemetryParams.closePublishDialogParams.existing_credentials_dialog_cancelled) this.sendTelemetryAndClose();
  }

  render() {
    return <div>
      {this.renderDialogDescription()}
      {!this.state.hasCriticalError && this.renderDialogBody()}
      {this.renderAlertWidget()}
      {this.renderFooterButtons()}
    </div>;
  }
}

export function open(views: IContentInfo[], server: SessionManagementService): Promise<any> {
  const closeDialog = () => {
    modalInstance && modalInstance.close();
  };
  const modalInstance = ReactModalService.open({
    title: Messages.publishToSalesforcePublishDialogTitle(),
    testId: PublishToSalesforceDialogTestIds.Dialog,
    type: DialogType.NormalWithTitleBar,
    showCloseButton: true,
    dismissDialogOnCloseAction: true,
    dialogStyle: PublishToSalesforceDialogStyles.DialogStyle,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogContent: <PublishToSalesforceDialog views={views} sessionManagementService={server} close={closeDialog} />,
  });
  return modalInstance.result;
}
